///
/// EarthBrowser
///
/// Created by Matt Giger on 10/18/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Fragment texture unit accounting for the earth tile shader. WebGL2
/// guarantees 16 sampler bindings per fragment stage; earthtile.glsl uses
/// several itself and each weather overlay adds its own. A program that
/// asks for more fails to link, and the globe simply stops drawing, so the
/// count is predicted here from the overlay shader defines - both to pick
/// a cheaper shader variant (EarthTileRenderer) and to refuse an enable
/// that could not be rendered (textureUnitGateMiddleware). Keep the table
/// in step with the `uniform sampler2D` declarations in the shaders.
///

import { type DatasetType } from "@/types/DatasetElement"

/** Minimum MAX_TEXTURE_IMAGE_UNITS WebGL2 guarantees; budget against it */
export const cFragmentTextureUnits = 16

/**
 * earthtile.glsl's own fragment samplers: tile imagery, parent imagery,
 * relief, parent relief, night lights, water mask
 */
export const cTileFragmentTextureUnits = 6

/** Units saved by sampling the water mask in the vertex stage instead */
export const cWaterMaskVertexSavings = 1

/** Fragment samplers each overlay define brings (frame pairs are packed into one RG texture) */
const cOverlayUnitsByDefine: Record<string, number> = {
	CLOUD_TEXTURES: 3,			// cloudField (baked advected pair), cloudRegion0, cloudRegion1 (storm cutouts)
	RADAR_TEXTURES: 2,			// radarPair, radarFlow
	RADAR_FORECAST_TEXTURES: 3,	// radarFcstPair, radarFcstFlow, radarFcstProbPair
	IMERG_TEXTURES: 2,			// imergPair, imergFlow
	SST_TEXTURES: 1,			// sstPair
	TEMPERATURE_TEXTURES: 2,	// temperature0/1 (not packed; no live material yet)
	SNOW_TEXTURES: 2,			// snow0/1 (not packed; no live material yet)
}

/** Fragment samplers the overlay defines add to the tile shader */
export function overlayTextureUnits(defines: Record<string, string>): number {
	let units = 0
	for (const [name, value] of Object.entries(defines)) {
		if (name === "GROUND_OVERLAY_COUNT") {
			units += parseInt(value) || 0
		} else {
			units += cOverlayUnitsByDefine[name] ?? 0
		}
	}
	return units
}

/** Total fragment samplers of the tile shader compiled with these defines */
export function tileFragmentTextureUnits(defines: Record<string, string>): number {
	return cTileFragmentTextureUnits + overlayTextureUnits(defines)
}

/**
 * Overlay defines a dataset puts on the tile shader when enabled.
 * Mirrors the DatasetLayer -> TextureOverlay wiring: "precipitation" turns
 * on both the MRMS radar mosaic and the IMERG satellite estimate
 */
const cDatasetOverlayDefines: Partial<Record<DatasetType, string[]>> = {
	cloud: ["CLOUD_TEXTURES"],
	// Sub-layer of clouds: draws through the cloud overlay's region samplers
	cloud_forecast: ["CLOUD_TEXTURES"],
	radar: ["RADAR_TEXTURES"],
	// Sub-layer of radar: its chunk relies on radar_common from RADAR_TEXTURES
	radar_forecast: ["RADAR_TEXTURES", "RADAR_FORECAST_TEXTURES"],
	precipitation: ["RADAR_TEXTURES", "IMERG_TEXTURES"],
	sst: ["SST_TEXTURES"],
}

/** Whether enabling this dataset adds samplers to the tile shader */
export function isOverlayDataset(type: DatasetType): boolean {
	return cDatasetOverlayDefines[type] !== undefined
}

/**
 * Defines the tile shader would carry with these datasets on. Duplicate
 * defines collapse the way combineDefines merges them in the renderer
 */
export function overlayDefinesForDatasets(types: DatasetType[]): Record<string, string> {
	const defines: Record<string, string> = {}
	for (const type of types) {
		for (const define of cDatasetOverlayDefines[type] ?? []) {
			defines[define] = "1"
		}
	}
	return defines
}

export interface TextureUnitVerdict {
	allowed: boolean
	/** Fragment samplers the tile shader would need with the dataset on */
	units: number
	budget: number
}

/**
 * Whether the tile shader can still be linked with `type` enabled on top
 * of `visible`. Ground image overlays are not in redux and are capped
 * separately (GroundOverlays), so they are not counted here; the vertex
 * water-mask fallback is credited since the renderer applies it as needed
 */
export function canEnableDatasetTextureUnits(type: DatasetType, visible: DatasetType[]): TextureUnitVerdict {
	const budget = cFragmentTextureUnits + cWaterMaskVertexSavings
	if (!isOverlayDataset(type)) {
		return { allowed: true, units: 0, budget }
	}
	const units = tileFragmentTextureUnits(overlayDefinesForDatasets([...visible, type]))
	return { allowed: units <= budget, units, budget }
}
